import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  cancelRolePermissionCleanupPlan,
  getRolePermissionCleanupPlan,
  getRolePermissionCleanupPlans,
  markRolePermissionCleanupReady,
} from '../api/permissionCleanupApi';
import { colorTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';

const { semantic } = colorTokens;

const CLEANUP_STATUSES = ['preview', 'scheduled', 'ready', 'canceled', 'deferred'];

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const cancelOutlineSx = {
  ...rowActionSx,
  color: semantic.error,
  border: `1px solid ${semantic.error}`,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: 'rgba(211, 47, 47, 0.08)',
    borderColor: semantic.error,
    color: semantic.error,
  },
};

function CleanupPlanDetailDialog({ open, onClose, planId, onRefresh, canManage }) {
  const { t } = useTranslation(['permissionCleanup', 'common', 'subscriptionChanges']);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !planId) return;
    setLoading(true);
    getRolePermissionCleanupPlan(planId)
      .then(setPlan)
      .finally(() => setLoading(false));
  }, [open, planId]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('permissionCleanup:viewDetail')} #{planId}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress size={24} />}
        {plan && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('permissionCleanup:previewOnly')}</Alert>
            {plan.status === 'ready' && (
              <Alert severity="warning">{t('permissionCleanup:cleanupNotApplied')}</Alert>
            )}
            <Typography variant="body2">{t('common:status')}: {plan.status}</Typography>
            <Typography variant="body2">{t('permissionCleanup:triggerType')}: {plan.trigger_type}</Typography>
            <Typography variant="body2">
              {t('permissionCleanup:removedModules')}: {(plan.removed_modules || []).join(', ')}
            </Typography>
            <Typography variant="body2">
              {t('permissionCleanup:activeModulesAfterChange')}: {(plan.active_modules_snapshot || []).join(', ')}
            </Typography>
            <Typography variant="subtitle2">
              {t('permissionCleanup:permissionsToRemove')} ({plan.permissions_to_remove_count})
            </Typography>
            {(plan.permissions_to_remove || []).slice(0, 20).map((item) => (
              <Typography key={item.permission} variant="body2" sx={{ fontFamily: 'monospace' }}>
                {item.permission} — {item.module_code}
              </Typography>
            ))}
            <Typography variant="subtitle2">
              {t('permissionCleanup:permissionsPreserved')} ({plan.permissions_to_preserve_count})
            </Typography>
            {(plan.warnings || []).map((warning) => (
              <Alert key={warning} severity="warning">{warning}</Alert>
            ))}
            {plan.apply_blocked_reason && (
              <Typography variant="body2">
                {t('permissionCleanup:applyBlockedReason')}: {plan.apply_blocked_reason}
              </Typography>
            )}
            {plan.subscription_change_request_id && (
              <Button
                variant="Secondary"
                component={RouterLink}
                to="/billing/subscription-changes"
                sx={rowActionSx}
              >
                {t('permissionCleanup:linkedChangeRequest')} #{plan.subscription_change_request_id}
              </Button>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canManage && plan.status === 'scheduled' && (
                <Button
                  variant="Secondary"
                  onClick={() => markRolePermissionCleanupReady(plan.id).then(onRefresh)}
                  sx={rowActionSx}
                >
                  {t('permissionCleanup:markReady')}
                </Button>
              )}
              {canManage && ['scheduled', 'ready', 'preview'].includes(plan.status) && (
                <Button
                  variant="Ghost"
                  onClick={() => cancelRolePermissionCleanupPlan(plan.id).then(onRefresh)}
                  sx={cancelOutlineSx}
                >
                  {t('permissionCleanup:cancelPlan')}
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RolePermissionCleanupPlansPage() {
  const { t } = useTranslation(['permissionCleanup', 'billing', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.billing.permission_cleanup.manage');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailId, setDetailId] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = items.length;

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = statusFilter ? { status: statusFilter } : {};
    getRolePermissionCleanupPlans(params)
      .then((response) => setItems(response.data || []))
      .catch(() => setError(t('permissionCleanup:loadFailed')))
      .finally(() => setLoading(false));
  }, [statusFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        id: 'tenant',
        label: t('billing:tenant'),
        render: (row) => row.tenant?.name || row.tenant_id || emptyValue,
      },
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (row) => row.customer?.name || row.customer_id || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="generic"
                apiLabel={t(`permissionCleanup:${row.status}`, { defaultValue: row.status })}
                size="Sm"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'trigger',
        label: t('permissionCleanup:triggerType'),
        render: (row) => row.trigger_type || emptyValue,
      },
      {
        id: 'removed_modules',
        label: t('permissionCleanup:removedModules'),
        render: (row) => {
          const modules = row.removed_modules || [];
          return modules.length ? modules.join(', ') : emptyValue;
        },
      },
      {
        id: 'permissions_count',
        label: t('permissionCleanup:permissionsCount'),
        render: (row) => (
          row.permissions_to_remove_count === 0 || row.permissions_to_remove_count
            ? row.permissions_to_remove_count
            : emptyValue
        ),
      },
      {
        id: 'effective_at',
        label: t('permissionCleanup:effectiveAt'),
        render: (row) => (
          row.effective_at
            ? new Date(row.effective_at).toLocaleString()
            : emptyValue
        ),
      },
      {
        id: 'can_apply',
        label: t('permissionCleanup:canApply'),
        render: (row) => (
          <StatusBadge
            status={row.can_apply ? 'yes' : 'no'}
            category="generic"
            apiLabel={row.can_apply ? t('common:yes') : t('common:no')}
            size="Sm"
            tone={row.can_apply ? 'Success' : 'Neutral'}
          />
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button
              variant="Ghost"
              onClick={() => setDetailId(row.id)}
              sx={rowActionSx}
            >
              {t('permissionCleanup:viewDetail')}
            </Button>
            {canManage && row.status === 'scheduled' && (
              <Button
                variant="Ghost"
                onClick={() => cancelRolePermissionCleanupPlan(row.id).then(load)}
                sx={cancelOutlineSx}
              >
                {t('permissionCleanup:cancelPlan')}
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [canManage, emptyValue, load, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('permissionCleanup:title')}
        subtitle={t('permissionCleanup:subtitle')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/billing">
            {t('common:dashboard')}
          </Button>
        )}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('permissionCleanup:previewOnly')}
      </Alert>

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {CLEANUP_STATUSES.map((status) => (
            <FilterSelectOption key={status} value={status}>
              {t(`permissionCleanup:${status}`)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('permissionCleanup:title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('permissionCleanup:toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={items}
            getRowKey={(row) => row.id}
            emptyMessage={t('permissionCleanup:noPlans')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <CleanupPlanDetailDialog
        open={Boolean(detailId)}
        onClose={() => setDetailId(null)}
        planId={detailId}
        onRefresh={() => { setDetailId(null); load(); }}
        canManage={canManage}
      />
    </Box>
  );
}
